// frontend/src/components/planner/SplitRulesPanel.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Zap,
  Building2,
  Percent,
  Search
} from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';

export default function SplitRulesPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rules, setRules] = useState([]);
  const [entities, setEntities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    merchant_pattern: '',
    category_pattern: '',
    personal_percentage: 50,
    business_entity_id: '',
    business_percentage: 50,
    business_category_code: '',
    reason: '',
    auto_apply: false
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      const [rulesRes, entitiesRes] = await Promise.all([
        api.get('/api/split-rules?includeInactive=true'),
        api.get('/api/business-entities')
      ]);
      setRules(rulesRes.data.rules || []);
      setEntities(entitiesRes.data.businessEntities || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load split rules');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      merchant_pattern: '',
      category_pattern: '',
      personal_percentage: 50,
      business_entity_id: entities[0]?.id || '',
      business_percentage: 50,
      business_category_code: '',
      reason: '',
      auto_apply: false
    });
    setEditingRule(null);
    setShowForm(false);
  }

  function openAdd() {
    resetForm();
    setFormData(prev => ({ ...prev, business_entity_id: entities[0]?.id || '' }));
    setShowForm(true);
  }

  function openEdit(rule) {
    setEditingRule(rule);
    setFormData({
      name: rule.name || '',
      merchant_pattern: rule.merchant_pattern || '',
      category_pattern: rule.category_pattern || '',
      personal_percentage: parseFloat(rule.personal_percentage),
      business_entity_id: rule.business_entity_id,
      business_percentage: parseFloat(rule.business_percentage),
      business_category_code: rule.business_category_code || '',
      reason: rule.reason || '',
      auto_apply: rule.auto_apply
    });
    setShowForm(true);
  }

  function handlePctChange(field, value) {
    const val = Math.max(0, Math.min(100, parseFloat(value) || 0));
    const other = field === 'personal_percentage' ? 'business_percentage' : 'personal_percentage';
    setFormData(prev => ({ ...prev, [field]: val, [other]: Math.round((100 - val) * 100) / 100 }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (!formData.business_entity_id) return;

    try {
      setSaving(true);
      if (editingRule) {
        await api.patch(`/api/split-rules/${editingRule.id}`, formData);
      } else {
        await api.post('/api/split-rules', formData);
      }
      resetForm();
      await loadData();
    } catch (err) {
      console.error('Error saving rule:', err);
      alert(err.response?.data?.error || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(rule) {
    if (!window.confirm(`Delete split rule "${rule.name}"?`)) return;
    try {
      await api.delete(`/api/split-rules/${rule.id}`);
      await loadData();
    } catch (err) {
      console.error('Error deleting rule:', err);
      alert('Failed to delete rule');
    }
  }

  async function handleToggleActive(rule) {
    try {
      await api.patch(`/api/split-rules/${rule.id}`, { is_active: !rule.is_active });
      await loadData();
    } catch (err) {
      console.error('Error toggling rule:', err);
      alert('Failed to update rule');
    }
  }

  async function handleToggleAutoApply(rule) {
    try {
      await api.patch(`/api/split-rules/${rule.id}`, { auto_apply: !rule.auto_apply });
      await loadData();
    } catch (err) {
      console.error('Error toggling auto-apply:', err);
      alert('Failed to update rule');
    }
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Expense Split Rules</h1>
            <p className="text-base sm:text-sm text-gray-500 mt-1">
              Define how shared expenses are split between personal and business
            </p>
          </div>
          <button
            onClick={openAdd}
            disabled={entities.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-medium min-h-[44px]"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>

        {entities.length === 0 && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-base sm:text-sm text-amber-800">
              <strong>No business entities found.</strong> Create a business entity first in the{' '}
              <button onClick={() => navigate('/planner/business')} className="underline font-medium">
                Business Entities
              </button>{' '}
              panel before setting up split rules.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-base sm:text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-lg shadow p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingRule ? 'Edit Split Rule' : 'New Split Rule'}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              {/* Row 1: Name + Entity */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Home Office Internet"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Entity *</label>
                  <select
                    value={formData.business_entity_id}
                    onChange={e => setFormData(prev => ({ ...prev, business_entity_id: parseInt(e.target.value, 10) }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  >
                    <option value="">Select entity...</option>
                    {entities.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Patterns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Search className="w-3.5 h-3.5 inline mr-1" />
                    Merchant Pattern
                  </label>
                  <input
                    type="text"
                    value={formData.merchant_pattern}
                    onChange={e => setFormData(prev => ({ ...prev, merchant_pattern: e.target.value }))}
                    placeholder="e.g. %COMCAST% or %AMAZON%"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Use % as wildcard. Matches merchant name.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Search className="w-3.5 h-3.5 inline mr-1" />
                    Category Pattern
                  </label>
                  <input
                    type="text"
                    value={formData.category_pattern}
                    onChange={e => setFormData(prev => ({ ...prev, category_pattern: e.target.value }))}
                    placeholder="e.g. %Utilities% or %Office%"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Matches Plaid transaction categories.</p>
                </div>
              </div>

              {/* Row 3: Percentages */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Percent className="w-3.5 h-3.5 inline mr-1" />
                    Personal %
                  </label>
                  <input
                    type="number"
                    min="0" max="100" step="0.01"
                    value={formData.personal_percentage}
                    onChange={e => handlePctChange('personal_percentage', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Percent className="w-3.5 h-3.5 inline mr-1" />
                    Business %
                  </label>
                  <input
                    type="number"
                    min="0" max="100" step="0.01"
                    value={formData.business_percentage}
                    onChange={e => handlePctChange('business_percentage', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Visual split bar */}
              <div className="h-3 rounded-full overflow-hidden flex bg-gray-100">
                <div
                  className="bg-blue-400 transition-all"
                  style={{ width: `${formData.personal_percentage}%` }}
                  title={`Personal: ${formData.personal_percentage}%`}
                />
                <div
                  className="bg-purple-500 transition-all"
                  style={{ width: `${formData.business_percentage}%` }}
                  title={`Business: ${formData.business_percentage}%`}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span className="text-blue-600">Personal {formData.personal_percentage}%</span>
                <span className="text-purple-600">Business {formData.business_percentage}%</span>
              </div>

              {/* Row 4: Reason + Category Code */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Description</label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="e.g. Home office, Vehicle use"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Category Code</label>
                  <input
                    type="text"
                    value={formData.business_category_code}
                    onChange={e => setFormData(prev => ({ ...prev, business_category_code: e.target.value }))}
                    placeholder="e.g. 6250 for Utilities"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Auto-apply toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, auto_apply: !prev.auto_apply }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.auto_apply ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.auto_apply ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <span className="text-base sm:text-sm font-medium text-gray-700">Auto-apply</span>
                  <p className="text-xs text-gray-400">Automatically split matching transactions during sync</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-base sm:text-sm font-medium min-h-[44px]"
                >
                  {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Rules List */}
        {loading ? (
          <LoadingSpinner />
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Percent className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-lg font-medium text-gray-500">No split rules yet</p>
            <p className="text-base sm:text-sm mt-1">Create rules to split shared expenses between personal and business</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map(rule => {
              const entity = rule.businessEntity || entities.find(e => e.id === rule.business_entity_id);
              const pPct = parseFloat(rule.personal_percentage);
              const bPct = parseFloat(rule.business_percentage);

              return (
                <div
                  key={rule.id}
                  className={`bg-white border rounded-lg shadow p-4 transition-opacity ${
                    !rule.is_active ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{rule.name}</h3>
                        {rule.auto_apply && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                            <Zap className="w-3 h-3" />
                            Auto
                          </span>
                        )}
                        {!rule.is_active && (
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                            Inactive
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                        {entity && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {entity.name}
                          </span>
                        )}
                        {rule.merchant_pattern && (
                          <span className="flex items-center gap-1">
                            <Search className="w-3.5 h-3.5" />
                            Merchant: {rule.merchant_pattern}
                          </span>
                        )}
                        {rule.category_pattern && (
                          <span className="flex items-center gap-1">
                            <Search className="w-3.5 h-3.5" />
                            Category: {rule.category_pattern}
                          </span>
                        )}
                        {rule.reason && <span className="italic">{rule.reason}</span>}
                      </div>

                      {/* Split bar */}
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 rounded-full overflow-hidden flex bg-gray-100 max-w-xs">
                          <div className="bg-blue-400" style={{ width: `${pPct}%` }} />
                          <div className="bg-purple-500" style={{ width: `${bPct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {pPct}% / {bPct}%
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleToggleAutoApply(rule)}
                        title={rule.auto_apply ? 'Disable auto-apply' : 'Enable auto-apply'}
                        className={`p-2 sm:p-1.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                          rule.auto_apply
                            ? 'text-amber-600 hover:bg-amber-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        <Zap className="w-5 h-5 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(rule)}
                        title={rule.is_active ? 'Deactivate' : 'Activate'}
                        className="p-2 sm:p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      >
                        {rule.is_active ? <ToggleRight className="w-5 h-5 sm:w-4 sm:h-4 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 sm:w-4 sm:h-4" />}
                      </button>
                      <button
                        onClick={() => openEdit(rule)}
                        className="p-2 sm:p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      >
                        <Edit2 className="w-5 h-5 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule)}
                        className="p-2 sm:p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      >
                        <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
